import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  useTheme,
  Avatar,
  Chip,
  Rating,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
} from '@mui/material';
import {
  SentimentVerySatisfied as SatisfiedIcon,
  SentimentSatisfied as NeutralIcon,
  SentimentDissatisfied as DissatisfiedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Loyalty as LoyaltyIcon,
  Timer as TimerIcon,
  AttachMoney as MoneyIcon,
  Repeat as RepeatIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  Legend,
} from 'recharts';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import LocationSelector from '../components/LocationSelector';
import BrandSelector from '../components/BrandSelector';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerAnalytics: React.FC = () => {
  const theme = useTheme();
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);

  // Mock data
  const customerMetrics = {
    totalCustomers: 15420,
    newCustomers: 342,
    returningRate: 68.5,
    avgSatisfaction: 4.3,
    nps: 72,
    avgOrderValue: 28.50,
    avgVisitFrequency: 2.3,
    loyaltyMembers: 8945,
  };

  const satisfactionTrend = [
    { month: 'Jan', score: 4.1, reviews: 230 },
    { month: 'Feb', score: 4.2, reviews: 245 },
    { month: 'Mar', score: 4.3, reviews: 280 },
    { month: 'Apr', score: 4.3, reviews: 295 },
  ];

  const customerSegments = [
    { segment: 'Loyal Customers', value: 35, color: '#af2326' },
    { segment: 'Regular Visitors', value: 30, color: '#ff6b35' },
    { segment: 'Occasional', value: 25, color: '#1976d2' },
    { segment: 'First Time', value: 10, color: '#4caf50' },
  ];

  const feedbackCategories = [
    { category: 'Food Quality', positive: 85, negative: 15, total: 450 },
    { category: 'Service Speed', positive: 78, negative: 22, total: 380 },
    { category: 'Staff Friendliness', positive: 92, negative: 8, total: 320 },
    { category: 'Cleanliness', positive: 88, negative: 12, total: 280 },
    { category: 'Value for Money', positive: 82, negative: 18, total: 310 },
  ];

  const topComplaints = [
    { issue: 'Wait time too long', count: 45, trend: 'up' },
    { issue: 'Order accuracy', count: 32, trend: 'down' },
    { issue: 'Food temperature', count: 28, trend: 'stable' },
    { issue: 'Staff attitude', count: 15, trend: 'down' },
    { issue: 'Cleanliness', count: 12, trend: 'down' },
  ];

  const loyaltyMetrics = {
    totalMembers: 8945,
    activeMembers: 6234,
    avgPointsBalance: 245,
    redemptionRate: 65,
    memberSpendVsNon: 185, // percentage
  };

  const visitFrequency = [
    { frequency: 'Daily', customers: 120, avgSpend: 45 },
    { frequency: '2-3x/week', customers: 890, avgSpend: 35 },
    { frequency: 'Weekly', customers: 2340, avgSpend: 28 },
    { frequency: 'Monthly', customers: 3456, avgSpend: 22 },
    { frequency: 'Rarely', customers: 2139, avgSpend: 18 },
  ];

  const daypartPreferences = [
    { daypart: 'Breakfast', percentage: 15 },
    { daypart: 'Lunch', percentage: 45 },
    { daypart: 'Dinner', percentage: 35 },
    { daypart: 'Late Night', percentage: 5 },
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSentimentIcon = (score: number) => {
    if (score >= 4) return <SatisfiedIcon sx={{ color: theme.palette.success.main }} />;
    if (score >= 3) return <NeutralIcon sx={{ color: theme.palette.warning.main }} />;
    return <DissatisfiedIcon sx={{ color: theme.palette.error.main }} />;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Customer Analytics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Customer satisfaction, loyalty, and behavior insights
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ minWidth: 250 }}>
              <BrandSelector value={selectedBrand} onChange={(brand) => setSelectedBrand(brand as string)} />
            </Box>
            <Box sx={{ minWidth: 300 }}>
              <LocationSelector value={selectedLocation} onChange={setSelectedLocation} selectedBrand={selectedBrand} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(175, 35, 38, 0.2)' }}>
                  <SatisfiedIcon sx={{ color: theme.palette.primary.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg Satisfaction
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {customerMetrics.avgSatisfaction}
                    </Typography>
                    <Rating value={customerMetrics.avgSatisfaction} precision={0.1} size="small" readOnly />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                      +0.2 from last month
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(255, 107, 53, 0.2)' }}>
                  <LoyaltyIcon sx={{ color: '#ff6b35' }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    NPS Score
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {customerMetrics.nps}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={customerMetrics.nps}
                    sx={{
                      mt: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: '#ff6b35',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.3 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(25, 118, 210, 0.2)' }}>
                  <RepeatIcon sx={{ color: theme.palette.info.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Return Rate
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {customerMetrics.returningRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {customerMetrics.avgVisitFrequency}x per month avg
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(76, 175, 80, 0.2)' }}>
                  <MoneyIcon sx={{ color: theme.palette.success.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg Order Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    ${customerMetrics.avgOrderValue}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                      +$2.30 from last year
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Satisfaction & Feedback" />
          <Tab label="Customer Segments" />
          <Tab label="Loyalty Program" />
          <Tab label="Behavior Insights" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Satisfaction Trend */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Customer Satisfaction Trend
                </Typography>
                <IconButton size="small">
                  <DownloadIcon />
                </IconButton>
              </Box>
              
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={satisfactionTrend}>
                  <defs>
                    <linearGradient id="satisfactionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#af2326" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#af2326" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="month" stroke="#A0AEC0" />
                  <YAxis domain={[0, 5]} stroke="#A0AEC0" />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="#af2326"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#satisfactionGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Top Complaints */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top Customer Complaints
              </Typography>
              
              <Box>
                {topComplaints.map((complaint, index) => (
                  <Box
                    key={complaint.issue}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      mb: 1,
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: 2,
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        #{index + 1}
                      </Typography>
                      <Typography variant="body2">
                        {complaint.issue}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {complaint.count}
                      </Typography>
                      {complaint.trend === 'up' && <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />}
                      {complaint.trend === 'down' && <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* Feedback Categories */}
          <Grid item xs={12}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Feedback by Category
              </Typography>
              
              <Grid container spacing={2}>
                {feedbackCategories.map((category) => (
                  <Grid item xs={12} md={6} lg={2.4} key={category.category}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        {category.category}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getSentimentIcon(category.positive / 20)}
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {category.positive}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={category.positive}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: category.positive >= 85 ? theme.palette.success.main : theme.palette.warning.main,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {category.total} reviews
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Customer Segments */}
          <Grid item xs={12} md={6}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Customer Segmentation
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={customerSegments}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {customerSegments.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 2 }}>
                {customerSegments.map((segment) => (
                  <Box key={segment.segment} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: segment.color }} />
                      <Typography variant="body2">{segment.segment}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {segment.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* Visit Frequency */}
          <Grid item xs={12} md={6}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Visit Frequency Analysis
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={visitFrequency}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="frequency" stroke="#A0AEC0" />
                  <YAxis yAxisId="left" stroke="#A0AEC0" />
                  <YAxis yAxisId="right" orientation="right" stroke="#A0AEC0" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="customers" fill="#af2326" name="Customers" />
                  <Bar yAxisId="right" dataKey="avgSpend" fill="#1976d2" name="Avg Spend ($)" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          {/* Daypart Preferences */}
          <Grid item xs={12}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Customer Visit Patterns by Daypart
              </Typography>
              
              <Grid container spacing={2}>
                {daypartPreferences.map((daypart) => (
                  <Grid item xs={12} sm={6} md={3} key={daypart.daypart}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {daypart.percentage}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {daypart.daypart}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Loyalty Overview */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...glassStyle, p: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Members
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loyaltyMetrics.totalMembers.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    +12% from last quarter
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...glassStyle, p: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Active Members
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loyaltyMetrics.activeMembers.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {((loyaltyMetrics.activeMembers / loyaltyMetrics.totalMembers) * 100).toFixed(1)}% engagement
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...glassStyle, p: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Avg Points Balance
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loyaltyMetrics.avgPointsBalance}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${(loyaltyMetrics.avgPointsBalance / 10).toFixed(2)} value
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ ...glassStyle, p: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Member vs Non-Member Spend
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loyaltyMetrics.memberSpendVsNon}%
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Members spend more
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Loyalty Tiers */}
          <Grid item xs={12}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Loyalty Program Tiers
              </Typography>
              
              <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tier</TableCell>
                      <TableCell>Members</TableCell>
                      <TableCell>Avg Spend/Month</TableCell>
                      <TableCell>Retention Rate</TableCell>
                      <TableCell>Benefits Redeemed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { tier: 'Platinum', members: 450, avgSpend: 185, retention: 95, redeemed: 89 },
                      { tier: 'Gold', members: 1280, avgSpend: 125, retention: 88, redeemed: 76 },
                      { tier: 'Silver', members: 3456, avgSpend: 85, retention: 82, redeemed: 65 },
                      { tier: 'Bronze', members: 3759, avgSpend: 45, retention: 75, redeemed: 45 },
                    ].map((tier) => (
                      <TableRow key={tier.tier}>
                        <TableCell>
                          <Chip label={tier.tier} size="small" />
                        </TableCell>
                        <TableCell>{tier.members.toLocaleString()}</TableCell>
                        <TableCell>${tier.avgSpend}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={tier.retention}
                              sx={{
                                width: 60,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  backgroundColor: theme.palette.success.main,
                                },
                              }}
                            />
                            <Typography variant="body2">{tier.retention}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{tier.redeemed}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          {/* Purchase Patterns */}
          <Grid item xs={12}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Customer Purchase Patterns
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="visits" name="Monthly Visits" stroke="#A0AEC0" />
                  <YAxis dataKey="spend" name="Avg Spend" stroke="#A0AEC0" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter
                    name="Customers"
                    data={[
                      { visits: 1, spend: 15 },
                      { visits: 2, spend: 22 },
                      { visits: 3, spend: 28 },
                      { visits: 4, spend: 32 },
                      { visits: 5, spend: 38 },
                      { visits: 8, spend: 45 },
                      { visits: 12, spend: 52 },
                      { visits: 15, spend: 58 },
                      { visits: 20, spend: 65 },
                    ]}
                    fill="#af2326"
                  />
                </ScatterChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(175, 35, 38, 0.1)', borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Insight:</strong> Customers who visit more frequently tend to have higher average order values, 
                  indicating loyalty program effectiveness.
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default CustomerAnalytics;